// Steer the eFiling document-type picker away from case-initiation-only
// ("Initial …") filing codes on SUBSEQUENT filings, where they don't apply and
// the clerk rejects them.
//
// Madera cert Sub #3 (2026-07-01): a subsequent custody RFO went in as JTI code
// 244114 "Initial Request for Order — Custody/Visitation" and the clerk bounced
// it — on an existing case the correct code is the plain "Request for Order —
// Custody/Visitation" (no "Initial"). This helper drops the initiation-only
// variant from the picker on subsequent filings, but only when its non-initial
// counterpart is available, so lone "(Initial)" codes with no counterpart stay
// visible.
//
// Pure / deterministic — no clock/crypto, order-preserving. EFM-agnostic.
/**
 * Normalize a document-type name to its "base" (initiation-agnostic) form so an
 * "Initial …" variant and its plain counterpart compare equal:
 * - strip a "(Initial)" parenthetical (anywhere)
 * - strip a leading "Initial " marker
 * - normalize em/en dashes to a hyphen and collapse whitespace
 * - lowercase
 */
export function baseDocTypeName(name) {
    return name
        .replace(/\(\s*initial\s*\)/gi, ' ')
        .replace(/^\s*initial\s+/i, '')
        .replace(/[—–]/g, '-')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();
}
/**
 * A document type is "initiation-only" when it is the case-initiation flavor of
 * a document. Two signals, combined (OR):
 * - structural: formGroups mark it case-initiation (EFCI / EFCI_LEAD) and NOT
 *   subsequent (EF_LEAD).
 * - nominal: the display name carries an "Initial" marker (a leading "Initial "
 *   or a "(Initial)" parenthetical).
 *
 * formGroups alone is unreliable on the subsequent side (WOL-349: Madera's
 * subsequent doc types mostly carry EMPTY formGroups), so the name heuristic
 * backstops the structural signal.
 */
export function isInitiationOnlyDocType(dt) {
    const fg = dt.formGroups ?? [];
    const structural = (fg.includes('EFCI') || fg.includes('EFCI_LEAD')) && !fg.includes('EF_LEAD');
    const nominal = /^\s*initial\b/i.test(dt.name) || /\(\s*initial\s*\)/i.test(dt.name);
    return structural || nominal;
}
/**
 * On a subsequent filing, drop each initiation-only document type IFF a
 * non-initiation counterpart (same base name) also exists in the list. The
 * counterpart gate keeps this safe: a lone "… (Initial)" with no plain sibling
 * (e.g. some Probate petitions) stays visible. Case-initiation filings
 * (`isSubsequent === false`) are returned unchanged. Order-preserving.
 */
export function steerAwayFromInitiationOnly(docTypes, isSubsequent) {
    if (!isSubsequent)
        return docTypes;
    // Base names that have at least one NON-initiation-only entry — the available
    // counterparts a filer should use instead.
    const counterpartBases = new Set();
    for (const dt of docTypes) {
        if (!isInitiationOnlyDocType(dt))
            counterpartBases.add(baseDocTypeName(dt.name));
    }
    return docTypes.filter(dt => !(isInitiationOnlyDocType(dt) && counterpartBases.has(baseDocTypeName(dt.name))));
}
